import { Link } from 'theme-ui';
import { ChangeEvent, FormEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { observer } from 'mobx-react-lite';
import styles from '@features/footer/Footer.styles';
import theme from '@styles/theme';
import { useNewsletterStore } from '@features/footer/components/FooterNewsletter/mobx/providers/NewsletterStoreProvider';
import Icon from '@components/icons/Icon';
import Button from '@components/Button/Button';
import Checkbox from '@components/Checkbox/Checkbox';
import { DEFAULT_URL_PRIVACY_POLICY } from '@config/index';
import { focusWithoutVisibleRing, handlePointerInputFocus, handleKeyboardInputFocus, clearPointerInputFocus } from '@utils/focusWithoutVisibleRing';
const FooterNewsletter = () => {
  const {
    t
  } = useTranslation('layouts');
  const privacyPolicy = DEFAULT_URL_PRIVACY_POLICY;
  const {
    email,
    setEmail,
    inputActive,
    setInputActive,
    showThx,
    errorMessage,
    agreementValidate,
    termsAgreement,
    setTermsAgreement,
    submitNewsletterForm,
    sendingProgress
  } = useNewsletterStore();
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submitNewsletterForm();
  };
  const handleInputEvent = (e: ChangeEvent<HTMLInputElement>) => {
    setInputActive(true);
    setEmail(e.currentTarget.value);
  };
  const activateInput = () => {
    setInputActive(true);
    focusWithoutVisibleRing(inputRef.current);
  };
  useEffect(() => {
    const checkIfClickedOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (formRef.current?.contains(target)) return;
      if (inputActive && !email?.length) setInputActive(false);
    };
    window.addEventListener('click', checkIfClickedOutside);
    return () => window.removeEventListener('click', checkIfClickedOutside);
  }, [inputActive, email, setInputActive]);
  const hidePlaceholder = inputActive || !!email?.length;
  return <div sx={styles.footerNewsletterContainer}>
      {showThx ? <div sx={styles.newsletterThx}>{errorMessage || t('footer.newsletter.thx')}</div> : <form ref={formRef} action="" onSubmit={(e: FormEvent<HTMLFormElement>) => handleSubmit(e)}>
          <div sx={styles.newsletterForm}>
            {!hidePlaceholder && <button type="button" aria-controls="newsletter-email-input" onMouseDown={e => {
          e.preventDefault();
          activateInput();
        }} sx={styles.newsletterPlaceholder}>
                {t('footer.newsletter.inputPlaceholder')}
              </button>}
            <input ref={inputRef} id="newsletter-email-input" onMouseDown={handlePointerInputFocus} onKeyDown={handleKeyboardInputFocus} onFocus={() => {
          setInputActive(true);
        }} onBlur={e => {
          clearPointerInputFocus(e);
          if (!email?.length) setInputActive(false);
        }} sx={styles.newsletterInput} type="email" value={email} name="newsletterEmail" onChange={handleInputEvent} aria-label={t('footer.newsletter.inputPlaceholder')} />
            <Button variant="iconOnly" size="small" loading={sendingProgress} type="submit">
              <Icon icon="ArrowForward" size="24px" color={theme.rawColors.darkGrey700} />
            </Button>
          </div>
          {errorMessage && <div sx={styles.newsletterError}>{t(errorMessage)}</div>}
          <div sx={styles.newsletterCheckbox}>
            <Checkbox value={termsAgreement} onChange={value => setTermsAgreement(value)} error={t('footer.newsletter.inputTermsAgreement')} showError={!agreementValidate}>
              <div sx={styles.newsletterPolicyWrapper}>
                {t('footer.newsletter.privacyAgreement')}{' '}
                <Link href={privacyPolicy} target="_blank" rel="noreferrer" sx={styles.newsletterPolicyLink} title={`${t('footer.newsletter.privacyLink')} ${t('footer.links.opensInNewWindow')}`}>
                  {t('footer.newsletter.privacyLink')}
                </Link>
                {t('footer.newsletter.subscriptionNote')}
              </div>
            </Checkbox>
          </div>
        </form>}
    </div>;
};
export default observer(FooterNewsletter);